import type { ScreenSpaceFlags } from '@post-office/placement-common/pipes';
import {
	createClientPipeline,
	createServerPipeline,
} from '@post-office/placement-common/utils/create-pipeline'; // NOTE: TEMPORARY FIX You must add this yourself

import * as assets from '@post-office/growth-common/rovo-third-party-apps-assets';

import { type MessageTemplateContext } from '../../..';

export type ConnectorApp = {
	type: string;
	outboundAuthUrl: string;
	isUserOAuthed: boolean;
	providerId: string;
	integrationId: string;
	datasourceId: string;
	isImpersonationAuthEnabled: boolean;
	hasAuthUpgrade: boolean;
	productAri: string;
};

export type HydrationResult = {
	listOfSmartLinks: string[];
	listOfConnectorApps: (ConnectorApp & ConnectorProvider)[];
	product: string;
};

type Pipe = ScreenSpaceFlags<{
	messageContext: MessageTemplateContext;
	hydrationResult: HydrationResult;
}>;

export type ConnectorProvider = {
	providerType: string;
	providerLearnMoreUrl?: string;
	normalizedAppName: string;
	logo: string;
};

// List of 34 3P-connectors config
// TODO: update mapping key to be object provider key, as the current connector key is created only for the TAP trait
// e.g. "ms-power-bi-connector does not exist", but "ms-power-bi-object-provider" exists
// https://data-portal.internal.atlassian.com/targeting/traits/6c28de26-f1a4-45df-9f33-c8969c6db65b
// slightly outdated external support page: https://support.atlassian.com/platform-experiences/docs/smart-links-from-jira-and-other-products/

export const ConnectorProvidersMapping: Record<string, ConnectorProvider> = {
	'google-drive-connector': {
		// done
		providerType: 'smartlinks-connector-gdrive',
		providerLearnMoreUrl:
			'https://support.atlassian.com/organization-administration/docs/connect-google-drive-to-rovo/',
		normalizedAppName: 'Google Drive',
		logo: assets.googleDriveLogo,
	},
	'salesforce-connector': {
		// done
		providerType: 'smartlinks-connector-salesforce',
		// No learn more page for Salesforce
		normalizedAppName: 'Salesforce',
		logo: assets.salesforceLogo,
	},
	'airtable-connector': {
		// done
		providerType: 'smartlinks-connector-airtable',
		providerLearnMoreUrl:
			'https://support.atlassian.com/organization-administration/docs/connect-airtable-to-rovo/',
		normalizedAppName: 'Airtable',
		logo: assets.airtableLogo,
	},
	'figma-connector': {
		// done
		providerType: 'smartlinks-connector-figma',
		// No learn more page for Figma
		normalizedAppName: 'Figma',
		logo: assets.figmaLogo,
	},
	'gitlab-connector': {
		// done
		providerType: 'smartlinks-connector-gitlab',
		providerLearnMoreUrl:
			'https://support.atlassian.com/organization-administration/docs/connect-gitlab-to-rovo/',
		normalizedAppName: 'GitLab',
		logo: assets.gitlabLogo,
	},
	'miro-connector': {
		// done
		providerType: 'smartlinks-connector-miro',
		providerLearnMoreUrl:
			'https://support.atlassian.com/organization-administration/docs/connect-miro-to-rovo/',
		normalizedAppName: 'Miro',
		logo: assets.miroLogo,
	},
	'asana-connector': {
		// done
		providerType: 'smartlinks-connector-asana',
		providerLearnMoreUrl:
			'https://support.atlassian.com/organization-administration/docs/connect-asana-to-rovo/',
		normalizedAppName: 'Asana',
		logo: assets.asanaLogo,
	},
	'smartsheet-connector': {
		// done
		providerType: 'smartlinks-connector-smartsheet',
		providerLearnMoreUrl:
			'https://support.atlassian.com/organization-administration/docs/connect-smartsheet-to-rovo/',
		normalizedAppName: 'Smartsheet',
		logo: assets.smartsheetLogo,
	},
	'dropbox-connector': {
		// done
		providerType: 'smartlinks-connector-dropbox',
		providerLearnMoreUrl:
			'https://support.atlassian.com/organization-administration/docs/connect-dropbox-to-rovo/',
		normalizedAppName: 'Dropbox',
		logo: assets.dropboxLogo,
	},
	'box-connector': {
		// done
		providerType: 'smartlinks-connector-box',
		providerLearnMoreUrl:
			'https://support.atlassian.com/organization-administration/docs/connect-box-to-rovo/',
		normalizedAppName: 'Box',
		logo: assets.boxLogo,
	},
	'lucid-connector': {
		// done
		providerType: 'smartlinks-connector-lucid',
		providerLearnMoreUrl:
			'https://support.atlassian.com/organization-administration/docs/connect-lucid-to-rovo/',
		normalizedAppName: 'Lucid',
		logo: assets.lucidLogo,
	},
	'hubspot-connector': {
		// done
		providerType: 'smartlinks-connector-hubspot',
		// No learn more page for HubSpot
		normalizedAppName: 'HubSpot',
		logo: assets.hubspotLogo,
	},
	'docusign-connector': {
		providerType: 'smartlinks-connector-docusign',
		providerLearnMoreUrl:
			'https://support.atlassian.com/organization-administration/docs/connect-docusign-to-rovo/',
		normalizedAppName: 'Docusign',
		logo: assets.docusignLogo,
	},
	'slack-connector': {
		// done
		providerType: 'smartlinks-connector-slack',
		providerLearnMoreUrl:
			'https://support.atlassian.com/organization-administration/docs/connect-slack-to-rovo/',
		normalizedAppName: 'Slack',
		logo: assets.slackLogo,
	},
	'microsoft-teams-connector': {
		// done
		providerType: 'smartlinks-connector-teams',
		providerLearnMoreUrl:
			'https://support.atlassian.com/organization-administration/docs/connect-microsoft-teams-to-rovo/',
		normalizedAppName: 'Microsoft Teams',
		logo: assets.msTeamsLogo,
	},
	'azure-devops-connector': {
		// done
		providerType: 'smartlinks-connector-azure-devops',
		providerLearnMoreUrl:
			'https://support.atlassian.com/organization-administration/docs/connect-azure-devops-to-rovo/',
		normalizedAppName: 'Azure DevOps',
		logo: assets.azureLogo,
	},
	'notion-connector': {
		// done
		providerType: 'smartlinks-connector-notion',
		providerLearnMoreUrl:
			'https://support.atlassian.com/organization-administration/docs/connect-notion-to-rovo/',
		normalizedAppName: 'Notion',
		logo: assets.notionLogo,
	},
	'onedrive-connector': {
		// done
		providerType: 'smartlinks-connector-onedrive',
		providerLearnMoreUrl:
			'https://support.atlassian.com/organization-administration/docs/connect-onedrive-to-rovo/',
		normalizedAppName: 'OneDrive',
		logo: assets.onedriveLogo,
	},
	'sharepoint-connector': {
		// not done
		providerType: 'smartlinks-connector-sharepoint',
		providerLearnMoreUrl:
			'https://support.atlassian.com/organization-administration/docs/connect-sharepoint-to-rovo/',
		normalizedAppName: 'Microsoft SharePoint',
		logo: assets.sharepointSquareLogo,
	},
	'zendesk-connector': {
		providerType: 'smartlinks-connector-zendesk',
		providerLearnMoreUrl:
			'https://support.atlassian.com/organization-administration/docs/connect-zendesk-to-rovo/',
		normalizedAppName: 'Zendesk',
		logo: assets.zendeskLogo,
	},
	'adobe-xd-connector': {
		providerType: 'smartlinks-connector-adobexd',
		providerLearnMoreUrl:
			'https://support.atlassian.com/organization-administration/docs/connect-adobe-sign-to-rovo/',
		normalizedAppName: 'Adobe XD',
		logo: assets.adobeXdLogo,
	},
	'github-connector': {
		// done
		providerType: 'smartlinks-connector-github',
		providerLearnMoreUrl:
			'https://support.atlassian.com/organization-administration/docs/connect-github-to-rovo/',
		normalizedAppName: 'GitHub',
		logo: assets.githubLogo,
	},
	'amplitude-connector': {
		// done
		providerType: 'smartlinks-connector-amplitude',
		// No learn more page for Amplitude
		normalizedAppName: 'Amplitude',
		logo: assets.amplitudeLogo,
	},
	'clickup-connector': {
		providerType: 'smartlinks-connector-clickup',
		// No learn more page for ClickUp
		normalizedAppName: 'ClickUp',
		logo: assets.clickupLogo,
	},
	'dovetail-connector': {
		providerType: 'smartlinks-connector-dovetail',
		// No learn more page for Dovetail
		normalizedAppName: 'Dovetail',
		logo: assets.dovetailLogo,
	},
	'launchdarkly-connector': {
		// done
		providerType: 'smartlinks-connector-launchdarkly',
		// No learn more page for LaunchDarkly
		normalizedAppName: 'LaunchDarkly',
		logo: assets.launchdarklyLogo,
	},
	'mural-connector': {
		// done
		providerType: 'smartlinks-connector-mural',
		// No learn more page for Mural
		normalizedAppName: 'Mural',
		logo: assets.muralLogo,
	},
	'pagerduty-connector': {
		// done
		providerType: 'smartlinks-connector-pagerduty',
		// No learn more page for PagerDuty
		normalizedAppName: 'PagerDuty',
		logo: assets.pagerdutyLogo,
	},
	'pipedrive-connector': {
		// done
		providerType: 'smartlinks-connector-pipedrive',
		// No learn more page for Pipedrive
		normalizedAppName: 'Pipedrive',
		logo: assets.pipedriveLogo,
	},
	'ms-power-bi-connector': {
		// done
		providerType: 'smartlinks-connector-ms-power-bi',
		// No learn more page for PowerBI
		normalizedAppName: 'Power BI',
		logo: assets.msPowerBiLogo,
	},
	'sentry-connector': {
		// done
		providerType: 'smartlinks-connector-sentry',
		// No learn more page for Sentry
		normalizedAppName: 'Sentry',
		logo: assets.sentryLogo,
	},
	'stripe-connector': {
		// not done
		providerType: 'smartlinks-connector-stripe',
		// No learn more page for Stripe
		normalizedAppName: 'Stripe',
		logo: assets.stripeLogo,
	},
	'todoist-connector': {
		providerType: 'smartlinks-connector-todoist',
		// No learn more page for Todoist
		normalizedAppName: 'Todoist',
		logo: assets.todoistLogo,
	},
	'webex-connector': {
		// done
		providerType: 'smartlinks-connector-webex',
		// No learn more page for Webex
		normalizedAppName: 'WebEx',
		logo: assets.webexLogo,
	},
	'zeplin-connector': {
		// done
		providerType: 'smartlinks-connector-zeplin',
		// No learn more page for Zeplin
		normalizedAppName: 'Zeplin',
		logo: assets.zeplinLogo,
	},
};

export type Rovo3PAppsExperimentCohort = 'not-enrolled' | 'control' | 'variation';

export const { createRender, createValidate } = createClientPipeline<Pipe>();
export const { createHydrate, createHydrateExample, createTransformMetadata } =
	createServerPipeline<Pipe>();
